/*eslint max-lines-per-function: ["error", 230]*/
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

import Button from '../../../common/components/Button';
import MultiSelectSortable, {
  ReactSelectOption,
} from '../Selects/SortableSelect';
import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateHarmfulFactorDto,
  CreateUpdateHarmfulFactorDtoCategoryEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateHarmfulFactorDto';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  HarmfulFactorCategory,
  HarmfulFactorCategoryText,
} from '../../../common/spdCore/validation/schemas';
import SupportingExaminationDictMultiSelector from '../SupportingExaminationDict/SupportingExaminationDictMultiSelector';
import { GetSupportingExaminationDictDto } from '../../../common/spdCore/autogenerated/spdApiClient';

interface HarmfulFactorEditorProps {
  id?: string;
  harmfulFactor: CreateUpdateHarmfulFactorDto;
  handleClose?: () => void;
  show?: boolean;
  selectedTests?: ReactSelectOption[];
  allTests: ReactSelectOption[];
  supportsExaminationDicts?: GetSupportingExaminationDictDto[];
  onCompleted: (
    harmfulFactor: CreateUpdateHarmfulFactorDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const HarmfulFactorEditor: React.FC<
  HarmfulFactorEditorProps
> = props => {
  const [harmfulFactor, setHarmfulFactor] = useState(props.harmfulFactor);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [supports, setSupports] = useState<ReactSelectOption[]>([]);
  const [selectedTestIds, setSelectedTestIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const spd = useSpdCore();

  useEffect(() => {
    const keys = props.selectedTests?.map(o => o.value);
    if (keys) {
      setSelectedTestIds(keys);
    }
  }, [props.selectedTests]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);

      const supp =
        props.supportsExaminationDicts?.map(b => ({
          value: b.id,
          label: b.name || '',
        })) || [];
      setHarmfulFactor({
        ...props.harmfulFactor,
        supportingExaminationsIds: supp.map(o => o.value),
      });
      setSupports(supp);
    }
  }, [
    props.show,
    props.harmfulFactor,
    props.id,
    props.supportsExaminationDicts,
  ]);

  const createHarmfulFactor = async () => {
    setIsSaving(true);
    props
      .onCompleted({ ...harmfulFactor, testsIds: selectedTestIds }, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
      })
      .catch(error => {
        setSaveErrors(error);
        setShowSaveErrors(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const propertyChange = (obj: Partial<CreateUpdateHarmfulFactorDto>) => {
    setHarmfulFactor({ ...harmfulFactor, ...obj });
  };

  const getAllHarmfulFactorCategories = () => {
    const arr = Object.keys(HarmfulFactorCategory)

      .map(name => {
        return {
          value:
            HarmfulFactorCategory[name as keyof typeof HarmfulFactorCategory],
          label:
            HarmfulFactorCategoryText[
              name as keyof typeof HarmfulFactorCategory
            ],
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return arr;
  };

  const getSelectedHarmfulFactorCategory = () => {
    return getAllHarmfulFactorCategories().filter(
      item =>
        item.value ===
        (props.harmfulFactor.category as unknown as HarmfulFactorCategory),
    );
  };

  const handleClickSupports = async (tempSupports: ReactSelectOption[]) => {
    setHarmfulFactor({
      ...harmfulFactor,
      supportingExaminationsIds: tempSupports.map(o => o.value),
    });
    setSupports(tempSupports);
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? 'Edycja czynnika szkodliwego'
              : 'Dodawanie nowych czynników szkodliwych'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-2">
            <Card.Header>Czynnik szkodliwy</Card.Header>
            <Card.Body>
              <Form className="d-grid gap-3">
                <FormRow controlId="name" label="Nazwa">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={harmfulFactor?.name || ''}
                    onChange={e => propertyChange({ name: e.target.value })}
                  />
                </FormRow>
                <FormRow controlId="description" label="Opis">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={harmfulFactor?.description || ''}
                    onChange={e =>
                      propertyChange({ description: e.target.value })
                    }
                  />
                </FormRow>
                <FormRow controlId="template" label="Kategoria">
                  <Select
                    options={getAllHarmfulFactorCategories()}
                    defaultValue={getSelectedHarmfulFactorCategory()}
                    onChange={options =>
                      propertyChange({
                        category:
                          (options?.value as unknown as CreateUpdateHarmfulFactorDtoCategoryEnum) ||
                          null,
                      })
                    }
                    isClearable={true}
                  />
                </FormRow>
                <FormRow controlId="supportingExams" label="Badania pomocnicze">
                  <SupportingExaminationDictMultiSelector
                    multi={true}
                    selectedSupportingExamDict={supports}
                    onChangeMulti={options =>
                      handleClickSupports((options || []).map(o => o))
                    }
                    // type={SupportingType.specialisedConsultation}
                  />
                </FormRow>
                <FormRow controlId="tests" label="Testy">
                  <MultiSelectSortable
                    options={props.allTests}
                    defaultValue={props.selectedTests || []}
                    onChange={options =>
                      setSelectedTestIds((options || []).map(o => o.value))
                    }
                  />
                </FormRow>
                <FormRow controlId="user_active" label="Aktywny">
                  <Form.Check
                    type="checkbox"
                    checked={harmfulFactor?.active ? true : false}
                    onChange={e => propertyChange({ active: e.target.checked })}
                  />
                </FormRow>
                <FormRow
                  controlId="yearsForJudgment"
                  label={'Max. czas orzeczenia'}
                >
                  <Form.Control
                    type="number"
                    min={0}
                    value={harmfulFactor?.yearsForJudgment ?? ''}
                    onChange={e =>
                      propertyChange({
                        yearsForJudgment: parseInt(e.target.value, 10),
                      })
                    }
                  />
                </FormRow>
              </Form>
            </Card.Body>
          </Card>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={spd.harmfulFactors.getLastEditor}
            />
          )}
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          <Button
            variant="outline-primary"
            onClick={createHarmfulFactor}
            isLoading={isSaving}
          >
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
